import type { MethodLocale } from '../types'

export const zhMethod: MethodLocale = {
  hero: {
    tag: '方法论 · Method',
    title: 'Ontology 为什么真的有效',
    subtitle: '一篇长读 · Demo 看的、Lab 玩的,这里说清楚',
    leadParagraph:
      'Demo 是看,Lab 是玩,这一页讲清楚为什么这套东西能落地——ontology 是什么、为什么大模型单跑不够用、ontology 补了什么、企业落地具体怎么做。',
  },
  whatIs: {
    sectionTag: '§ A · 是什么',
    title: 'Ontology 是企业的',
    titleSpan: '数字镜像',
    leadLine: '一句话定义',
    leadBody:
      'Ontology 是企业现实世界在数字空间的镜像——对象、关系、约束、行为,被系统化地表达出来,让 AI Agent 可以在运行时基于它推理和执行,而不是在设计时背一堆硬编码的工作流。',
    comparisonTitle: 'Ontology vs 传统知识图谱',
    comparisonHeaderKg: '传统知识图谱',
    comparisonHeaderOnto: 'Ontology',
    comparisonRows: [
      { label: '理解"是什么"', kg: '是', onto: '是' },
      { label: '理解"怎么做"', kg: '—', onto: '是' },
      { label: '随业务演进', kg: '困难', onto: '原生支持' },
      { label: '触发业务操作', kg: '—', onto: '是,通过 Action' },
      { label: '推理方式', kg: '静态查询', onto: '动态推理(本体 + 大模型)' },
      { label: '建模原语', kg: '实体 · 关系 · 属性', onto: '属性图 + Action + Logic' },
    ],
    layersTitle: '本体的三层',
    layersHint: '企业本体不是一层静态的元数据。同一个模型同时承载三种行为。',
    layers: [
      {
        tag: '陈述性',
        name: '业务知道什么',
        description: '实体、关系、事件、属性。把企业静态知识做结构化表达。',
      },
      {
        tag: '程序性',
        name: '业务怎么做',
        description: '查询、工作流、自主推理。把业务逻辑和操作流程沉淀成系统能直接执行的东西。',
      },
      {
        tag: '动态',
        name: '业务此刻在做什么',
        description: '实时状态、上下文、最近事件。本体随业务演进,不需要重新训练大模型。',
      },
    ],
    exampleTag: '一个具象例子',
    exampleTitle: '航空公司问"这班为什么延误?"',
    exampleBody:
      '传统做法:把维护记录、气象数据、值机统计调出来,在表格里人工比对。本体支撑的 Agent 已经理解了"航班"、"飞机"、"天气"、"地勤"以及它们的关系——直接问"找出因前序航班晚点导致的延误,并展示维护记录和天气状况",因果链立即生成,"调度备用飞机"也是一键动作。',
  },
  whyMatters: {
    sectionTag: '§ B · 为什么重要',
    title: '为什么大模型',
    titleSpan: '需要一个底座',
    lead: '通用大模型本质是概率机器。一旦把它接到企业栈上,六个问题立刻冒出来。',
    challengesTitle: '通用大模型直接落企业的六大挑战',
    challenges: [
      {
        number: '01',
        title: '幻觉累积',
        body: '30 步推理链,每步 5% 错误率,最终 80% 结果不对。工业流程承受不了这种概率。',
      },
      {
        number: '02',
        title: '输出不稳定',
        body: '同一问题不同次结果不一样。审计、合规、SOP 都需要确定性输出。',
      },
      {
        number: '03',
        title: '自主规划弱',
        body: '长任务需要框架。没有框架,Agent 会跑偏或提前停止。',
      },
      {
        number: '04',
        title: '不会主动响应',
        body: '只会被动应答。数据变化不会触发它,漂移不会引起警觉,工作流不会自动跑起来。',
      },
      {
        number: '05',
        title: '数据整合困难',
        body: 'ERP / MES / CRM 各自孤岛。同一个词在不同系统里含义不同。大模型自己接不上。',
      },
      {
        number: '06',
        title: '冷启动慢',
        body: '从"我们要这个 Agent"到"Agent 上线",定制工程动辄数周到数月。',
      },
    ],
    valuesTitle: 'Ontology 补上什么',
    valuesHint: '三大价值,对应上面的失效模式。',
    values: [
      {
        label: '语义统一',
        body: '一次性消除"是 Apple 公司还是苹果水果"的歧义。部门、系统、大模型共用一套定义。没有"ERP 说 X、MES 说 Y"。',
      },
      {
        label: '推理可信',
        body: '每一步都能追溯到一条规则或一个数据源。审计就绪不是事后改,是天生的。',
      },
      {
        label: '行为可控',
        body: '业务硬约束直接变成 Agent 硬约束。不是"请你不要幻觉",是"这条规则就是规则,你违反不了"。',
      },
    ],
    bridgeTag: '一句缩写',
    bridgeTitle: 'Ontology + LLM = 能落地的 Agent',
    bridgeBody:
      '大模型负责语言和推理,本体提供世界模型。任何一半都不够。Demo 的 L3 阶段就是这个意思:和 L1 / L2 是同一个大模型,只是下面多了一层本体。',
  },
  howToUse: {
    sectionTag: '§ C · 怎么落地',
    title: '企业到底要',
    titleSpan: '建什么',
    lead: '三种能力必须同时到位。缺一,其他两个也站不稳。',
    capabilitiesTitle: '三种必须的能力',
    capabilities: [
      {
        number: '01',
        title: '数据与系统整合',
        body: '把每个系统拉到同一个语义层。底下还有三个子层:语义层(实体统一)、动能层(行为触发)、动态层(规则约束)。',
      },
      {
        number: '02',
        title: '智能动态本体管理',
        body: '本体随业务演进保持活的状态。规则和约束随时更新,不需要重训大模型。领域专家可以直接修改模型。',
      },
      {
        number: '03',
        title: '精准 AI 应用生成',
        body: '应用从领域本体生成,不是从 prompt 生成。每个应用都是业务系统孪生,不是一个能聊的机器人。',
      },
    ],
    enginesTitle: 'Workflow + Agentic — 双引擎',
    enginesHint: '一种引擎不够。确定性工作和探索性工作长得根本不一样。',
    engines: [
      {
        tag: 'Workflow 引擎',
        name: '审计级、确定性流程',
        body: '固定流水线:数据摄入、月度结账、监管报告。路径已知,需要的是一致性和可追溯。',
      },
      {
        tag: 'Agentic 引擎',
        name: '探索性、动态规划任务',
        body: '调查、What-if、多步诊断。Agent 在运行时基于本体现场规划,而不是回放一段录好的脚本。',
      },
    ],
    enginesLinkLabel: '到 Lab 里看双引擎跑起来 →',
    contextTitle: '上下文工程 — 大模型实际看到什么',
    contextHint: '本体不是一堆数据。它按任务实时组装四类 context 给大模型。',
    contextRows: [
      { asset: '数据描述', includes: '实体、关系、事件、属性 — 结构化模型本身' },
      { asset: '数据操作', includes: 'Agent 被允许做的事(增 / 查 / 改 / 删) — 行为边界' },
      { asset: '逻辑与流程', includes: '工作流、业务规则、规划逻辑 — 程序性那一面' },
      { asset: '页面描述', includes: '列表、图表、详情页 — 系统自动生成的应用界面' },
    ],
  },
  adoption: {
    sectionTag: '§ D · 落地长什么样',
    title: '实际',
    titleSpan: '落地过程',
    lead: '没有银弹。但路径是熟悉的,周期是可预测的。',
    phasesTitle: '典型周期',
    phases: [
      { scope: '业务验证场景', duration: '1-2 周' },
      { scope: '通用场景', duration: '1 个月内' },
      { scope: '高复杂度场景', duration: '1-6 个月' },
    ],
    stepsTitle: '六个步骤',
    steps: [
      '业务需求确认',
      '数据接入与探查',
      '本体定义与知识梳理',
      '数据对齐与治理',
      '开发验证',
      '试运行与迭代上线',
    ],
    deploymentTitle: '部署模式',
    deploymentHint: '按数据敏感度选,不按流行词选。',
    deploymentRows: [
      { mode: '本地部署', fit: '金融 · 政务 · 医疗 · 强监管制造' },
      { mode: '云端部署', fit: '中小微 · 低敏感度场景 · 快速迭代' },
      { mode: '混合部署', fit: '不同业务单元敏感度不一,组合使用' },
    ],
  },
  beyondBasics: {
    sectionTag: '§ F · 更深的视角',
    title: '更深的视角 — ',
    titleSpan: '一线观察',
    lead: '三段更尖锐的观察,来自真实项目。补充 §A-§E,不是替代。',
    threeLayersTitle: '真正能落地的三层:Ontology · Harness · Agent',
    threeLayersHint:
      '模型厂商(OpenAI DeployCo、Anthropic 企业 Claude)都在卷"交付层",剩下的差距是语义层。这部分,每家企业必须自己补。',
    threeLayers: [
      {
        tag: 'Ontology',
        name: '业务语义层',
        body: '把对象、关系、规则、流程、权限——这些散在文档、系统和经验里的东西——抽到一个结构化的世界。是企业自己的活,不是模型厂商的活。',
      },
      {
        tag: 'Harness',
        name: '执行治理',
        body: '不是"更聪明",而是"更稳":边界、状态、校验、回滚、审计。模型厂商开始触及这层;大部分还在客户这边。',
      },
      {
        tag: 'Agent',
        name: '任务执行',
        body: '大家都在卷的这层。但是:下面两层没立稳,Agent 只能做零散动作。',
      },
    ],
    symptomsTitle: '"没有语义层"具体长什么样',
    symptomsHint: '企业接了模型 + Agent + 工具,但没有 Ontology——典型症状是可预测的:',
    symptoms: [
      '能回答问题,但回答进不了业务流程',
      '能调工具,但不知道什么时候该调、什么时候该停',
      '能生成内容,但口径很难长期一致',
      '能做一些局部工作,但跨系统就开始吃力',
      '能给出结果,但结果很难沉淀成组织资产',
    ],
    contrastTitle: '看起来在工作 vs 真的在参与业务',
    contrastHint: '六个维度区分"Demo 很惊艳"和"真到生产环境":',
    contrastHeaderShallow: '看起来在工作',
    contrastHeaderDeep: '真的在参与业务',
    contrastRows: [
      { label: '回答', shallow: '流畅', deep: '准' },
      { label: '决策', shallow: '不参与', deep: '提供判断路径 + 数据依据' },
      { label: '业务接入', shallow: '表面接口', deep: '进入业务流程' },
      { label: '跨系统', shallow: '容易吃力', deep: '在统一语义层上自然贯通' },
      { label: '沉淀', shallow: '一次性应答', deep: '持续转化为组织资产' },
      { label: '信任来源', shallow: '模型参数', deep: '"它真的理解你的业务"' },
    ],
    playbookCta: {
      tag: '操作手册',
      title: '读 Playbook',
      body: '落地 Ontology + Agent 的具体模式——从哪开始、怎么建本体、怎么选引擎、怎么管 Agent 行为、怎么保证可追溯、怎么搞冷启动。11 段,都来自真实项目。',
      cta: '进入 Playbook →',
    },
  },
  next: {
    sectionTag: '§ E · 下一步',
    title: '现在去',
    titleSpan: '看一下 · 玩一下 · 找你的行业',
    lead: '方法论读完了。接下来三个页面把它变成可以亲手摸的东西。',
    cards: [
      {
        tag: '看',
        title: 'Demo',
        body: '一段 L1 → L2 → L3 的解说式演示,基于石油采购场景。5 分钟英文自动 demo。',
        cta: '进入 Demo →',
        target: 'demo',
      },
      {
        tag: '玩',
        title: 'Lab',
        body: '十个交互式 playground,每个范式一个。点火警、丢文档、拖阈值,看链条实时重新合成。',
        cta: '进入 Lab →',
        target: 'lab',
      },
      {
        tag: '你的行业',
        title: '行业',
        body: '11 个行业 × 3 个范式,各自带行业专属数据。金融 · 医疗 · 法律 · 汽车 · 物流 · 政务 等等。',
        cta: '浏览行业 →',
        target: 'industries',
      },
    ],
  },
}
